import { Box, Container, GridItem } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React, { type ReactElement } from 'react'
import 'swiper/swiper-bundle.css'

import { getCourseMetaDescription, getCoursePageTitle } from '@/utils/seo'
import { makeServerProps } from '@/server/query'

import CourseQ from '@/queries/CourseQ'
import { COURSE_SLUG_SAT, MATH_COURSE_SLUGS, PILOT_DOMAIN } from '@/queries/constants'
import { useAppContext } from '@/client/context/app'
import { useQuery } from '@/client/context/query'
import PageNotFoundError from '@/client/error/PageNotFoundError'
import { SQL } from '@/client/query'
import makePage from '@/client/services/pages/makePage'
import { getCourseUrlBySlug, getExploreAllUrl } from '@/client/services/url'

import CourseFilters from '@/components/Course/CourseFilters/CourseFilters'
import CourseHeader from '@/components/Course/CourseHeader'
import CourseResults from '@/components/Course/CourseResults/CourseResults'
import CourseTopicsList from '@/components/Course/CourseResults/CourseTopicsList'
import ExploreFooter from '@/components/Explore/ExploreFooter/ExploreFooter'
import CourseBannerGraphic from '@/components/Icons/CourseBannerGraphic'
import Layout from '@/components/Layout/Layout'
import ShwGrid from '@/components/Layout/ShwGrid'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'

type CourseDetailTab = 'Sessions' | 'Topics'

function CourseIndex(): ReactElement {
  const appContext = useAppContext()
  const query = useQuery()
  const router = useRouter()

  const [currentTab, tabToUrl] = useUrlTabController<CourseDetailTab>({
    queryKey: 'show',
    tabToQueryParam: {
      Sessions: 'sessions',
      Topics: 'topics',
    },
    defaultTab: 'Sessions',
  })

  const [isLoading, setIsLoading] = React.useState(false)

  const isAllCourses = getExploreAllUrl().endsWith(String(router.pathname))

  const queryCourseSlug = isAllCourses ? null : String(router.query.course)
  const course = isAllCourses
    ? null
    : query.one(CourseQ.where({ slug: queryCourseSlug }, SQL`${CourseQ.domain} != ${PILOT_DOMAIN}`))

  const courseName = course == null ? null : course(CourseQ.name)
  const courseId = course == null ? 0 : (course(CourseQ.id) ?? 0)

  if (!appContext.dryRun && courseId === 0) {
    throw new PageNotFoundError('Oh no! This course does not exist')
  }

  const isMathCourse = MATH_COURSE_SLUGS.includes(queryCourseSlug ?? '')
  const isSATPrepCourse = queryCourseSlug === COURSE_SLUG_SAT
  const shouldHideSidebar = isMathCourse || isSATPrepCourse

  const pageTitle = getCoursePageTitle({ courseSlug: queryCourseSlug, courseName })

  return (
    <Layout
      title={pageTitle}
      ignoreTitleSuffix
      meta={{
        title: pageTitle,
        description: getCourseMetaDescription(queryCourseSlug),
      }}
      canonical={getCourseUrlBySlug(queryCourseSlug)}
    >
      <Box position="relative" bg="bg.subtle">
        <Box position="relative" zIndex={2}>
          <CourseHeader queryCourseSlug={queryCourseSlug} course={course} />
        </Box>
        <Box position="absolute" height={224} bottom={0} right={0} zIndex={1}>
          <CourseBannerGraphic />
        </Box>
      </Box>
      {!shouldHideSidebar ? (
        <Container>
          <ShwGrid>
            <GridItem colSpan={[null, null, 3]} display={['none', 'none', 'block']}>
              <CourseFilters course={course} disabled={isLoading} setPageDisabled={setIsLoading} />
            </GridItem>
            <GridItem colSpan={[12, 12, 9]} maxWidth="100%">
              <CourseResults
                queryCourseSlug={queryCourseSlug}
                course={course}
                disabled={isLoading}
                setPageDisabled={setIsLoading}
              />
            </GridItem>
          </ShwGrid>
        </Container>
      ) : (
        <Container>
          {isMathCourse ? (
            <Tabs<CourseDetailTab>
              currentTab={currentTab}
              tabToUrl={tabToUrl}
              tabs={['Sessions', 'Topics']}
              mb={4}
            />
          ) : null}
          {currentTab === 'Sessions' || isSATPrepCourse ? (
            <CourseResults
              queryCourseSlug={queryCourseSlug}
              course={course}
              disabled={isLoading}
              setPageDisabled={setIsLoading}
            />
          ) : course != null ? (
            <CourseTopicsList course={course} />
          ) : null}
        </Container>
      )}
      <ExploreFooter hideFaq={currentTab === 'Topics'} courseSlug={queryCourseSlug} hideLoggedIn />
    </Layout>
  )
}

const CourseIndexPage = makePage(CourseIndex, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(CourseIndexPage)
export default CourseIndexPage
